import React, { forwardRef, ReactNode } from 'react'
import { cx } from '../cx'
import s from './styles.module.scss'

export type CardTone = 'none' | 'good' | 'bad' | 'warn'

export interface CardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
    title?: ReactNode
    subtitle?: ReactNode
    /** Buttons/pills in the top right of the header. */
    actions?: ReactNode
    /** `none` for tables that should touch the card edge. */
    padding?: 'md' | 'sm' | 'none'
    footer?: ReactNode
    /** Grey background, for secondary/nested panels. */
    muted?: boolean
    /** Hover affordance for clickable cards. */
    interactive?: boolean
    /** 2px accent line on top (hero cards). */
    accentTop?: boolean
    /** Semantic left border. */
    tone?: CardTone
    as?: 'div' | 'section' | 'article'
    children?: ReactNode
}

/** White panel with hairline border. The base surface for everything. */
export const Card = forwardRef<HTMLDivElement, CardProps>(
    (
        {
            title,
            subtitle,
            actions,
            padding = 'md',
            footer,
            muted = false,
            interactive = false,
            accentTop = false,
            tone = 'none',
            as: Tag = 'div',
            className,
            children,
            ...rest
        },
        ref
    ) => (
        <Tag
            ref={ref as any}
            className={cx(
                s.card,
                padding === 'none' && s.padNone,
                padding === 'sm' && s.padSm,
                muted && s.muted,
                interactive && s.interactive,
                accentTop && s.accentTop,
                tone === 'good' && s.toneGood,
                tone === 'bad' && s.toneBad,
                tone === 'warn' && s.toneWarn,
                className
            )}
            {...rest}
        >
            {(title || subtitle || actions) && (
                <div className={s.head}>
                    <div className={s.headText}>
                        {title && <h3 className={s.title}>{title}</h3>}
                        {subtitle && <p className={s.subtitle}>{subtitle}</p>}
                    </div>
                    {actions && <div className={s.actions}>{actions}</div>}
                </div>
            )}
            {children != null && <div className={s.body}>{children}</div>}
            {footer && <div className={s.foot}>{footer}</div>}
        </Tag>
    )
)
Card.displayName = 'Card'

export interface CardGridProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Minimum column width; columns auto-fill. */
    minColumn?: number
    gap?: 'md' | 'sm'
    children: ReactNode
}

/** Responsive auto-fill grid for cards and stat tiles. */
export const CardGrid = ({ minColumn = 260, gap = 'md', className, style, children, ...rest }: CardGridProps) => (
    <div
        className={cx(s.grid, gap === 'sm' && s.gapSm, className)}
        style={{ ...style, ['--min-col' as any]: `${minColumn}px` }}
        {...rest}
    >
        {children}
    </div>
)

export const Divider = ({ className }: { className?: string }) => <hr className={cx(s.divider, className)} />
